import { FC, RefObject, useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Grow } from "./Layout/Grow";
import styled from "@emotion/styled";
import { useApp } from "../AppProvider";
import { useMobile } from "../MobileProvider";
import logo from "../assets/logo.webp";
import { logEvent } from "@firebase/analytics";
import { getDownloadURL, ref as firestoreRef } from "@firebase/storage";
import image from "../assets/hero-sliced/ezgif-frame-045.jpg";

const Wrapper = styled("section")(() => ({
  width: "100%",
  position: "relative",
  height: "120vh",
  "@media (max-width: 768px)": {
    height: "135vh",
  },
}));

const Background = styled("div")(() => ({
  background: "linear-gradient(rgba(0, 55, 255, 0) 0%, rgb(0, 7, 31) 100%)",
  zIndex: 0,
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  bottom: 0,
}));

const Video = styled("video", {
  shouldForwardProp: (prop) => prop !== "hide",
})<{ hide: boolean }>(({ hide }) => ({
  width: "100%",
  willChange: "transform",
  height: "150vh",
  objectFit: "cover",
  position: "fixed",
  top: "-20vh",
  left: 0,
  zIndex: -2,
  right: 0,
  display: "block",
  visibility: "visible",
  ...(hide && {
    display: "none",
    visibility: "hidden",
  }),
}));

const HeroStyled = styled("div")(() => ({
  width: "100%",
  willChange: "transform",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "4rem",
  "@media (min-width: 768px)": {
    flexDirection: "row",
  },
}));

const HeroBold = styled("h1")(() => ({
  color: "white",
  fontSize: 50,
  fontWeight: 400,
  textAlign: "center",
  marginBlockStart: "0.4em",
  marginBlockEnd: "0.4em",
  "@media (min-width: 768px)": {
    fontSize: 80,
    marginBlockStart: "0.9em",
    marginBlockEnd: "0.9em",
  },
  "@media (min-width: 1080px)": {
    fontSize: 100,
  },
  "@media (min-width: 1920px)": {
    fontSize: 120,
  },
  whiteSpace: "nowrap",
}));

const InfoText = styled("p")(() => ({
  color: "rgba(255,255,255,.7)",
  fontSize: 18,
  flexShrink: 1,
  minWidth: 300,
  fontFamily: "'Oswald', sans-serif",
}));

const Buttons = styled("div")(() => ({
  display: "flex",
  flexDirection: "column-reverse",
  gap: "1rem",
  justifyContent: "flex-start",
  alignItems: "start",
  width: "100%",
  maxWidth: 1500,
  "@media (min-width: 768px)": {
    flexDirection: "row",
    gap: "2rem",
    alignItems: "center",
  },
}));

const Button = styled("button")(() => ({
  padding: "1rem 2rem",
  borderRadius: 30,
  border: "1px solid white",
  outline: "none",
  color: "white",
  background: "transparent",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "0.3rem",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.1)",
  },
}));

const InfoTextWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "3rem",
}));

const TextWrapper = styled(motion.div)(() => ({
  display: "flex",
  willChange: "transform",
  width: "100%",
  flexWrap: "nowrap",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "4rem",
  position: "relative",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  "@media (min-width: 768px)": {
    position: "absolute",
    top: "40%",
    x: "-50%",
  },
}));

export const Hero: FC<{
  projekteRef: RefObject<HTMLDivElement | null>;
  aboutRef: RefObject<HTMLDivElement | null>;
}> = ({ projekteRef, aboutRef }) => {
  const { analytics, cookiesAccepted, storage, setModalVideoUrl } = useApp();
  const [showImage, setShowImage] = useState<boolean>(true);
  const ref = useRef<HTMLDivElement | null>(null);
  const mobile = useMobile();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["0%", "100%"],
  });
  const isInView = useInView(ref);
  const y = useTransform(
    scrollYProgress,
    [1, 0],
    ["0%", mobile ? "10%" : "-25%"]
  );
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancel = false;

    if (storage && cookiesAccepted) {
      if (!url) {
        getDownloadURL(firestoreRef(storage, "hero/hero.mp4"))
          .then((url) => {
            if (!cancel) {
              setUrl(url);
              setShowImage(false);
            }
          })
          .catch(() => {
            if (!cancel) {
              setShowImage(true);
            }
          });
      }
    } else {
      if (!cancel) {
        setShowImage(true);
      }
    }

    return () => {
      cancel = true;
    };
  }, [cookiesAccepted, storage, url]);

  const handleShowreel = () => {
    setModalVideoUrl(
      "https://player.vimeo.com/video/309255824?loop=1&byline=0&portrait=0&title=0&fun=0&autoplay=0&autopause=0&wmode=opaque&playsinline=1&player_id=ls-vimeo-1&dnt=1"
    );
    if (analytics) {
      logEvent(analytics, "showreel_click");
    }
  };

  return (
    <Wrapper ref={ref}>
      {url !== undefined && !showImage && (
        <Video autoPlay muted loop playsInline hide={!isInView}>
          <source src={url} type={"video/mp4"} />
        </Video>
      )}
      {showImage && (
        <img
          src={image}
          alt="hero"
          style={{
            objectFit: "cover",
            width: "100%",
            height: "100%",
            transform: "translateY(-10%)",
          }}
        />
      )}
      <Background />
      <TextWrapper
        style={{
          y,
        }}
        transition={{
          delay: 1,
          duration: 0.4,
          type: "spring",
          stiffness: 100,
          damping: 20,
        }}
      >
        <HeroStyled>
          <img src={logo} alt="logo" />
          <HeroBold>CENTRE FILMS</HeroBold>
        </HeroStyled>
        <Buttons>
          <Button
            type={"button"}
            onClick={() =>
              projekteRef.current?.scrollIntoView({ behavior: "smooth" })
            }
          >
            <span>Projekte</span>
            <ChevronDownIcon style={{ width: 16, height: 16 }} />
          </Button>
          <Button
            type={"button"}
            onClick={() =>
              aboutRef.current?.scrollIntoView({ behavior: "smooth" })
            }
          >
            <span>About</span>
            <ChevronDownIcon style={{ width: 16, height: 16 }} />
          </Button>
          <Grow />
          <InfoTextWrapper>
            <InfoText>
              CentreFilms – die Filmproduktion aus Dresden wurde 2014 gegründet
              und ist mittlerweile zu einer GmbH herangewachsen.
              <br />
              In unserem Leistungsportfolio finden Sie Werbefilme, Imagefilme,
              Webfilme, Unternehmerportraits, Industriefilme, Eventfilme,
              Livestreams, Reportagen und Dokumentationen, Musikvideos,
              Drohnenvideos oder auch Produktfilme – Wir setzen Ihre Ideen und
              Wünsche professionell um und begleiten das Projekt auf Wunsch auch
              fotografisch.
              <br />
              <br />– meet us and get to know us –
            </InfoText>
            <div>
              <Button onClick={handleShowreel} type={"button"}>
                Showreel anzeigen
              </Button>
            </div>
          </InfoTextWrapper>
        </Buttons>
      </TextWrapper>
    </Wrapper>
  );
};
